import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    StyleSheet,
    Text,
    View,
    TextInput,
    TouchableOpacity,
    ScrollView,
    Image,
} from 'react-native';
import moment from 'moment';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import DataTable from 'react-data-table-component';
import Modal from 'react-native-modal';
import { getStorage, setStorage } from '../storage';
import { useScale, ScaleFn , useDarkMode} from './common';

const INQUIRY_ORIGIN = 'https://moniter.smartdiaper.kr';
const INQUIRY_API = `${INQUIRY_ORIGIN}/api/customer-inquiry`;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
// 첨부 이미지 여러 장: URL 들을 콤마로 이어 IMAGE_URL(varchar255) 한 컬럼에 저장.
// URL 한 개가 약 44자라 varchar(255) 안에 안전하게 들어가는 5장으로 제한.
const MAX_IMAGES = 5;

async function postInquiry(path: string, body: any): Promise<any> {
    try {
        const res = await fetch(`${INQUIRY_API}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return await res.json();
    } catch (e) {
        console.warn('inquiry api error', e);
        return { error: 'network' };
    }
}

async function uploadImage(file: File): Promise<string | null> {
    try {
        const res = await fetch(`${INQUIRY_API}/upload`, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
        });
        const data = await res.json();
        return data?.url || null;
    } catch (e) {
        console.warn('upload error', e);
        return null;
    }
}

function pickImageFiles(): Promise<File[]> {
    return new Promise((resolve) => {
        const input: any = document.createElement('input');
        input.type = 'file';
        input.accept = ACCEPTED_TYPES.join(',');
        input.multiple = true;
        input.onchange = () => {
            resolve(input.files ? Array.from(input.files) as File[] : []);
        };
        input.click();
    });
}

const DARK = {
    bg: '#0F172A',
    card: '#1E293B',
    cardInner: '#0F172A',
    border: 'rgba(99,179,237,0.12)',
    text: '#E2E8F0',
    textSub: '#94A3B8',
    muted: '#a8adb497',
    trackBg: 'rgba(255,255,255,0.05)',
};
const LIGHT = {
    bg: '#F9FAFB',
    card: '#FFFFFF',
    cardInner: '#F3F4F6',
    border: '#E5E7EB',
    text: '#111827',
    textSub: '#4B5563',
    muted: '#9CA3AF',
    trackBg: 'rgba(0,0,0,0.03)',
};
const S = {
    accent: '#38BDF8',
    ok: '#34D399',
    err: '#F87171',
    warn: '#FB923C',
};

const CATEGORIES = ['제품 문의', '기술 지원', '계정/결제', '기능 요청', '기타'];

const DetailModal = ({ visible, onClose, inquiry, T }: any) => {
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    if (!inquiry) return null;
    const date = moment(inquiry?.REGDATE).format('YYYY-MM-DD HH:mm');
    const answerDate = inquiry?.ANSWER_DATE ? moment(inquiry.ANSWER_DATE).format('YYYY-MM-DD HH:mm') : null;
    const updateDate = inquiry?.UPDATE_DATE ? moment(inquiry.UPDATE_DATE).format('YYYY-MM-DD HH:mm') : null;
    return (
        <Modal
            style={{ flex: 1 }}
            backdropColor="black"
            isVisible={visible}
            backdropOpacity={0.7}
            onBackdropPress={onClose}
        >
            <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
                <View style={styles.modalHeaderRow}>
                    <View style={{ flex: 1, marginRight: s(12) }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8), marginBottom: s(6) }}>
                            {inquiry.CATEGORY ? (
                                <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(8) }}>
                                    <Text style={{ fontSize: s(11), fontWeight: '700', color: S.accent }}>{inquiry.CATEGORY}</Text>
                                </View>
                            ) : null}
                            <View style={{ backgroundColor: (inquiry.STATUS ? S.ok : S.warn) + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(8) }}>
                                <Text style={{ fontSize: s(11), fontWeight: '700', color: inquiry.STATUS ? S.ok : S.warn }}>
                                    {inquiry.STATUS ? '답변완료' : '답변대기'}
                                </Text>
                            </View>
                        </View>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }} numberOfLines={2}>
                            {inquiry?.TITLE}
                        </Text>
                    </View>
                    <TouchableOpacity
                        style={[styles.closeBtn, { backgroundColor: T.cardInner }]}
                        onPress={onClose}
                    >
                        <AntDesign name="close" size={s(16)} color={T.textSub} />
                    </TouchableOpacity>
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: s(16), gap: s(6) }}>
                    <Ionicons name="time-outline" size={s(14)} color={T.muted} />
                    <Text style={{ fontSize: s(13), color: T.textSub }}>{date}</Text>
                    {updateDate ? (
                        <Text style={{ fontSize: s(12), color: T.muted }}>({updateDate} 수정)</Text>
                    ) : null}
                </View>

                <View style={{ height: s(1), backgroundColor: T.border, marginBottom: s(16) }} />

                <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ paddingBottom: s(4) }} showsVerticalScrollIndicator={true}>
                <Text style={[styles.sectionLabel, { color: T.textSub }]}>문의 내용</Text>
                <ScrollView
                    style={[styles.contentBox, { backgroundColor: T.cardInner, borderColor: T.border }]}
                    showsVerticalScrollIndicator={true}
                >
                    <Text style={{ fontSize: s(14), color: T.text, lineHeight: s(22) }}>{inquiry?.CONTENT}</Text>
                </ScrollView>

                {inquiry?.IMAGE_URL ? (
                    <>
                        <Text style={[styles.sectionLabel, { color: T.textSub, marginTop: s(14) }]}>첨부 이미지</Text>
                        {String(inquiry.IMAGE_URL).split(',').map((u: string) => u.trim()).filter(Boolean).map((u: string, i: number) => (
                            <TouchableOpacity key={i} onPress={() => { if (typeof window !== 'undefined') window.open(INQUIRY_ORIGIN + u, '_blank'); }}>
                                <Image source={{ uri: INQUIRY_ORIGIN + u }} style={[styles.attachedImage, { marginBottom: s(8) }]} resizeMode="contain" />
                            </TouchableOpacity>
                        ))}
                    </>
                ) : null}

                {inquiry.ANSWER ? (
                    <>
                        <Text style={[styles.sectionLabel, { color: T.textSub, marginTop: s(14) }]}>답변 {answerDate ? `(${answerDate})` : ''}</Text>
                        <ScrollView
                            style={[styles.contentBox, { backgroundColor: S.accent + '12', borderColor: S.accent + '40' }]}
                            showsVerticalScrollIndicator={true}
                        >
                            <Text style={{ fontSize: s(14), color: T.text, lineHeight: s(22) }}>{inquiry.ANSWER}</Text>
                        </ScrollView>
                    </>
                ) : null}
                </ScrollView>

                <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: s(20) }}>
                    <TouchableOpacity
                        style={[styles.modalBtn, { backgroundColor: S.accent }]}
                        onPress={onClose}
                    >
                        <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>확인</Text>
                    </TouchableOpacity>
                </View>
            </View>
        </Modal>
    );
};

export default function CustomerInquiry({ navigation, route }: any) {
    const isFocused = useIsFocused();
    const { scale, s } = useScale();
    const styles = useMemo(() => makeStyles(s), [scale]);
    const { dark, setDark } = useDarkMode();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [sending, setSending] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [detailOpen, setDetailOpen] = useState(false);
    const [selected, setSelected] = useState<any>(null);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    // null = 새 문의 작성, 숫자 = 그 IDX 문의를 수정 중
    const [editIdx, setEditIdx] = useState<number | null>(null);
    // 화면 전체가 ScrollView 라 window.scrollTo 로는 안 올라간다 → 이 ref 로 직접 올린다.
    const scrollRef = useRef<any>(null);

    const userId = route?.params?.id || '';
    const hospitalName = route?.params?.hospital?.[0]?.NAME || '';
    const hospitalCode = route?.params?.hospital?.[0]?.CODE || '';

    const T = dark ? DARK : LIGHT;

    useEffect(() => {
        if (!isFocused) return;
        getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
        loadList();
    }, [isFocused]);

    const loadList = async () => {
        if (!userId) { setList([]); return; }
        const data = await postInquiry('/mine', { USER_ID: userId, HOSPITAL_CODE: hospitalCode });
        setList(data?.result || []);
    };

    const onPickImage = async () => {
        const files = await pickImageFiles();
        if (!files.length) return;
        const remain = MAX_IMAGES - imageUrls.length;
        if (remain <= 0) {
            alert(`이미지는 최대 ${MAX_IMAGES}장까지 첨부할 수 있습니다.`);
            return;
        }
        const targets = files.slice(0, remain);
        if (files.length > remain) {
            alert(`이미지는 최대 ${MAX_IMAGES}장까지 첨부할 수 있습니다. ${remain}장만 추가됩니다.`);
        }
        setUploading(true);
        for (const file of targets) {
            if (!ACCEPTED_TYPES.includes(file.type)) {
                alert(`${file.name}: PNG/JPG/WEBP/GIF 이미지만 업로드할 수 있습니다.`);
                continue;
            }
            if (file.size > MAX_IMAGE_BYTES) {
                alert(`${file.name}: 이미지 크기는 5MB 이하로 올려주세요.`);
                continue;
            }
            const url = await uploadImage(file);
            if (url) {
                setImageUrls((prev) => [...prev, url]);
                setImagePreviews((prev) => [...prev, URL.createObjectURL(file)]);
            } else {
                alert(`${file.name}: 이미지 업로드에 실패했습니다.`);
            }
        }
        setUploading(false);
    };

    const resetForm = () => {
        setTitle('');
        setContent('');
        setCategory(CATEGORIES[0]);
        setImageUrls([]);
        setImagePreviews([]);
        setEditIdx(null);
    };

    // 등록한 문의를 작성 폼으로 끌어와 수정 모드로 전환한다.
    const startEdit = (row: any) => {
        if (!row) return;
        if (row.STATUS) { alert('답변이 완료된 문의는 수정할 수 없습니다.'); return; }
        setDetailOpen(false);
        setEditIdx(row.IDX);
        setTitle(row.TITLE || '');
        setContent(row.CONTENT || '');
        setCategory(CATEGORIES.includes(row.CATEGORY) ? row.CATEGORY : CATEGORIES[0]);
        const urls = String(row.IMAGE_URL || '').split(',').map((u: string) => u.trim()).filter(Boolean);
        setImageUrls(urls);
        // 기존 첨부는 서버 경로라 origin 을 붙여야 미리보기가 뜬다(새로 올린 건 blob URL).
        setImagePreviews(urls.map((u: string) => INQUIRY_ORIGIN + u));
        scrollRef.current?.scrollTo?.({ y: 0, animated: true });
        // 바깥 페이지가 스크롤되는 경우(브라우저 폭이 좁을 때)를 위한 보조.
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        if (!confirm('수정을 취소할까요? 변경한 내용은 저장되지 않습니다.')) return;
        resetForm();
    };

    const removeImage = (idx: number) => {
        setImageUrls((prev) => prev.filter((_, i) => i !== idx));
        setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const onSubmit = async () => {
        if (!title.trim()) { alert('제목을 입력하세요.'); return; }
        if (!content.trim()) { alert('내용을 입력하세요.'); return; }
        if (!userId) { alert('로그인 정보를 확인할 수 없습니다.'); return; }
        if (sending || uploading) return;
        const editing = editIdx !== null;
        setSending(true);
        const data = await postInquiry(editing ? '/update' : '/submit', {
            ...(editing ? { IDX: editIdx } : {}),
            USER_ID: userId,
            HOSPITAL_CODE: hospitalCode,
            HOSPITAL_NAME: hospitalName,
            CATEGORY: category,
            TITLE: title,
            CONTENT: content,
            IMAGE_URL: imageUrls.length ? imageUrls.join(',') : null,
        });
        setSending(false);
        if (data?.success) {
            resetForm();
            alert(editing ? '문의가 수정되었습니다.' : '문의가 등록되었습니다. 확인 후 답변 드리겠습니다.');
            loadList();
        } else if (data?.error === 'answered') {
            // 폼을 열어둔 사이에 담당자가 답변을 달면 여기로 온다.
            alert('답변이 완료된 문의는 수정할 수 없습니다.');
            resetForm();
            loadList();
        } else {
            alert(editing ? '문의 수정에 실패했습니다. 잠시 후 다시 시도해주세요.' : '문의 등록에 실패했습니다. 잠시 후 다시 시도해주세요.');
        }
    };

    const columns = [
        { id: 1, name: 'No.', selector: (row: any) => row.IDX, grow: 0, width: '70px' },
        { id: 2, name: '분류', selector: (row: any) => row.CATEGORY || '-', grow: 0, width: '110px' },
        { id: 3, name: '제목', selector: (row: any) => row.TITLE, grow: 2 },
        {
            id: 4, name: '상태', grow: 0, width: '110px',
            cell: (row: any) => (
                <View style={{ backgroundColor: (row.STATUS ? S.ok : S.warn) + '18', paddingHorizontal: s(8), paddingVertical: s(3), borderRadius: s(8) }}>
                    <Text style={{ fontSize: s(11), fontWeight: '700', color: row.STATUS ? S.ok : S.warn }}>
                        {row.STATUS ? '답변완료' : '답변대기'}
                    </Text>
                </View>
            ),
        },
        {
            id: 5, name: '등록일', grow: 0, width: '130px',
            cell: (row: any) => (
                <View>
                    <Text style={{ fontSize: s(13), color: T.text }}>{moment(row.REGDATE).format('YYYY-MM-DD')}</Text>
                    {row.UPDATE_DATE ? (
                        <Text style={{ fontSize: s(11), color: T.muted }}>수정됨</Text>
                    ) : null}
                </View>
            ),
        },
        {
            id: 6, name: '관리', grow: 0, width: '100px',
            cell: (row: any) => (
                row.STATUS ? (
                    <Text style={{ fontSize: s(12), color: T.muted }}>—</Text>
                ) : (
                    <TouchableOpacity
                        // 셀 클릭이 행 클릭(상세 열기)으로 번지면 안 된다.
                        onPress={(e: any) => { e?.stopPropagation?.(); e?.preventDefault?.(); startEdit(row); }}
                        style={{
                            flexDirection: 'row', alignItems: 'center', gap: s(4),
                            paddingHorizontal: s(10), paddingVertical: s(5),
                            borderRadius: s(6), borderWidth: 1, borderColor: S.accent + '66',
                            backgroundColor: S.accent + '12',
                        }}
                    >
                        <Ionicons name="create-outline" size={s(13)} color={S.accent} />
                        <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>수정</Text>
                    </TouchableOpacity>
                )
            ),
        },
    ];

    const tableStyles = {
        headRow: { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
        headCells: { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
        rows: { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner, cursor: 'pointer' } } },
        cells: { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
        pagination: {
            style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
            pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } },
        },
        noData: { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
    };

    return (
        <ScrollView ref={scrollRef} style={{ flex: 1, backgroundColor: T.bg }} contentContainerStyle={{ padding: s(24), gap: s(20) }}>
            {/* ─── 문의 작성 카드 ─── */}
            <View style={[styles.cardWrap, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>
                            {editIdx !== null ? '문의 수정' : '1:1 문의하기'}
                        </Text>
                        {editIdx !== null ? (
                            <View style={{ backgroundColor: S.warn + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(8) }}>
                                <Text style={{ fontSize: s(11), fontWeight: '700', color: S.warn }}>No.{editIdx} 수정 중</Text>
                            </View>
                        ) : null}
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <TouchableOpacity
                            style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                            onPress={() => { navigation.navigate('Setting') }}
                        >
                            <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
                        </TouchableOpacity>
                    </View>
                </View>

                <Text style={[styles.helpText, { color: T.textSub }]}>
                    {editIdx !== null
                        ? '등록한 문의를 수정합니다. 답변이 완료되면 수정할 수 없습니다.'
                        : '챗봇으로 해결되지 않는 문의사항을 남겨주세요. 담당자가 확인 후 답변 드립니다.'}
                </Text>

                <Text style={[styles.label, { color: T.text }]}>분류</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: s(8), marginBottom: s(18) }}>
                    {CATEGORIES.map((c) => {
                        const active = category === c;
                        return (
                            <TouchableOpacity
                                key={c}
                                onPress={() => setCategory(c)}
                                style={{
                                    paddingHorizontal: s(14),
                                    paddingVertical: s(8),
                                    borderRadius: s(20),
                                    borderWidth: 1,
                                    borderColor: active ? S.accent : T.border,
                                    backgroundColor: active ? S.accent + '18' : T.cardInner,
                                }}
                            >
                                <Text style={{ fontSize: s(13), fontWeight: active ? '700' : '500', color: active ? S.accent : T.textSub }}>{c}</Text>
                            </TouchableOpacity>
                        );
                    })}
                </View>

                <Text style={[styles.label, { color: T.text }]}>제목</Text>
                <View style={[styles.inputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <TextInput
                        autoCapitalize="none"
                        placeholder="제목을 입력하세요."
                        placeholderTextColor={T.muted}
                        style={{ fontSize: s(14), color: T.text, flex: 1, paddingHorizontal: s(14), height: '100%', outlineStyle: 'none' } as any}
                        onChangeText={setTitle}
                        value={title}
                    />
                </View>

                <Text style={[styles.label, { color: T.text }]}>내용</Text>
                <View style={[styles.inputBox, styles.contentInputBox, { backgroundColor: T.cardInner, borderColor: T.border }]}>
                    <TextInput
                        autoCapitalize="none"
                        placeholder="문의 내용을 자세히 작성해주세요."
                        placeholderTextColor={T.muted}
                        multiline={true}
                        style={{ fontSize: s(14), color: T.text, flex: 1, padding: s(14), height: '100%', outlineStyle: 'none', textAlignVertical: 'top' } as any}
                        onChangeText={setContent}
                        value={content}
                    />
                </View>

                <Text style={[styles.label, { color: T.text }]}>이미지 첨부 (선택, 최대 5MB · 최대 {MAX_IMAGES}장)</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                    {imagePreviews.map((preview, idx) => (
                        <View key={idx} style={[styles.imagePreviewWrap, { borderColor: T.border, backgroundColor: T.cardInner, marginRight: s(8) }]}>
                            <Image source={{ uri: preview }} style={styles.imagePreview} resizeMode="cover" />
                            <TouchableOpacity style={styles.imageRemoveBtn} onPress={() => removeImage(idx)}>
                                <AntDesign name="close" size={s(14)} color="#fff" />
                            </TouchableOpacity>
                        </View>
                    ))}
                    {imageUrls.length < MAX_IMAGES ? (
                        <TouchableOpacity
                            style={[styles.imagePickBtn, { borderColor: T.border, backgroundColor: T.cardInner, opacity: uploading ? 0.6 : 1, width: s(220) }]}
                            onPress={onPickImage}
                            disabled={uploading}
                        >
                            <Ionicons name="image-outline" size={s(18)} color={T.textSub} style={{ marginRight: s(8) }} />
                            <Text style={{ fontSize: s(14), color: T.textSub }}>
                                {uploading ? '업로드 중...' : `이미지 첨부 (${imageUrls.length}/${MAX_IMAGES})`}
                            </Text>
                        </TouchableOpacity>
                    ) : null}
                </View>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: s(20) }}>
                    <Text style={{ fontSize: s(12), color: T.muted }}>
                        {userId ? `작성자: ${userId}${hospitalName ? ` · ${hospitalName}` : ''}` : '로그인 정보를 확인할 수 없습니다.'}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(8) }}>
                        {editIdx !== null ? (
                            <TouchableOpacity
                                style={[styles.sendBtn, { backgroundColor: T.cardInner, borderWidth: 1, borderColor: T.border }]}
                                onPress={cancelEdit}
                                disabled={sending || uploading}
                            >
                                <Text style={{ fontSize: s(14), fontWeight: '700', color: T.textSub }}>취소</Text>
                            </TouchableOpacity>
                        ) : null}
                        <TouchableOpacity
                            style={[styles.sendBtn, { opacity: (sending || uploading) ? 0.5 : 1 }]}
                            onPress={onSubmit}
                            disabled={sending || uploading}
                        >
                            <Ionicons name={editIdx !== null ? 'checkmark' : 'send'} size={s(14)} color="#fff" style={{ marginRight: s(6) }} />
                            <Text style={{ fontSize: s(14), fontWeight: '700', color: '#fff' }}>
                                {sending ? (editIdx !== null ? '수정 중...' : '등록 중...') : (editIdx !== null ? '수정 완료' : '등록')}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>

            {/* ─── 내 문의 내역 ─── */}
            <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
                <View style={styles.TopBox}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
                        <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>내 문의 내역</Text>
                        <Text style={{ fontSize: s(12), color: T.muted }}>답변 전에는 수정할 수 있습니다</Text>
                        {list?.length > 0 && (
                            <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{list.length}</Text>
                            </View>
                        )}
                    </View>
                    <TouchableOpacity
                        style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
                        onPress={loadList}
                    >
                        <Ionicons name="refresh" size={s(16)} color={T.textSub} />
                    </TouchableOpacity>
                </View>
                <DataTable
                    columns={columns as any}
                    data={list}
                    pagination
                    customStyles={tableStyles}
                    noDataComponent={
                        <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
                            <Text style={{ fontSize: s(14), color: T.muted }}>등록된 문의가 없습니다.</Text>
                        </View>
                    }
                    onRowClicked={(row: any) => { setSelected(row); setDetailOpen(true); }}
                />
            </View>

            <DetailModal
                visible={detailOpen}
                onClose={() => setDetailOpen(false)}
                inquiry={selected}
                T={T}
            />
        </ScrollView>
    );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
    cardWrap: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(24),
        paddingBottom: s(24),
    },
    dataTable: {
        borderRadius: s(16),
        borderWidth: 0.5,
        overflow: 'hidden',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: s(20),
        elevation: 3,
        paddingTop: s(20),
        paddingHorizontal: s(10),
    },
    TopBox: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 0,
        paddingBottom: s(12),
    },
    iconBtn: {
        width: s(34),
        height: s(34),
        borderRadius: s(10),
        justifyContent: 'center',
        alignItems: 'center',
    },
    helpText: {
        fontSize: s(13),
        lineHeight: s(20),
        marginBottom: s(18),
    },
    label: {
        fontSize: s(14),
        fontWeight: '600',
        marginBottom: s(8),
    },
    inputBox: {
        borderWidth: 1,
        borderRadius: s(10),
        height: s(42),
        marginBottom: s(18),
    },
    contentInputBox: {
        height: s(220),
    },
    sendBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#304156',
        borderRadius: s(10),
        paddingHorizontal: s(20),
        height: s(38),
    },
    modalView: {
        borderWidth: 1,
        borderRadius: s(20),
        padding: s(28),
        alignSelf: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: s(12),
        elevation: 8,
        width: '55%',
        maxWidth: s(700),
        maxHeight: '90%',
    },
    modalHeaderRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: s(8),
    },
    closeBtn: {
        width: s(30),
        height: s(30),
        borderRadius: s(8),
        justifyContent: 'center',
        alignItems: 'center',
    },
    sectionLabel: {
        fontSize: s(12),
        fontWeight: '600',
        marginBottom: s(6),
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    contentBox: {
        borderWidth: 1,
        borderRadius: s(12),
        padding: s(16),
        minHeight: s(120),
        maxHeight: s(280),
    },
    modalBtn: {
        paddingHorizontal: s(32),
        height: s(38),
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: s(10),
    },
    imagePickBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderRadius: s(10),
        height: s(52),
        marginBottom: s(8),
    },
    imagePreviewWrap: {
        position: 'relative',
        borderWidth: 1,
        borderRadius: s(10),
        overflow: 'hidden',
        alignSelf: 'flex-start',
        marginBottom: s(8),
    },
    imagePreview: {
        width: s(220),
        height: s(160),
    },
    imageRemoveBtn: {
        position: 'absolute',
        top: s(6),
        right: s(6),
        width: s(26),
        height: s(26),
        borderRadius: s(13),
        backgroundColor: 'rgba(0,0,0,0.6)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    attachedImage: {
        width: '100%',
        height: s(240),
        borderRadius: s(12),
        backgroundColor: '#000',
    },
});
